import React from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { getSourceLabel, getTicketFieldOptions, impliedTicketFields } from '../../utils/luckyDraw'
import Field from './Field'
import GroupList from './GroupList'
import { useGetLuckyDrawSources } from '../../api/lucky-draw'

const wholeNumber = (min, label) =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(min, `Must be at least ${min}`)
    .required(`${label} is required`)

const validationSchema = yup.object().shape({
  name: yup.string().required('Draw name is required'),
  source: yup.string().required('Source is required'),
  maxWinsPerDraw: wholeNumber(1, 'Max prizes per dealer'),
  groups: yup
    .array()
    .of(
      yup.object().shape({
        name: yup.string().required('Group name is required'),
        ticketFields: yup.array().of(yup.string()).min(1, 'Pick at least one ticket type'),
        maxWins: wholeNumber(1, 'Max per dealer'),
        prizes: yup
          .array()
          .of(
            yup.object().shape({
              name: yup.string().required('Prize name is required'),
              quantity: wholeNumber(1, 'Quantity'),
              winnersPerDraw: wholeNumber(1, 'Winners per draw'),
            }),
          )
          .min(1, 'Add at least one prize'),
      }),
    )
    .min(1, 'Add at least one group'),
})

// groupId/prizeId rather than id: useFieldArray writes its own generated key over an `id` field.
export const toFormValues = (draw) => ({
  name: draw.name,
  source: draw.source,
  maxWinsPerDraw: draw.maxWinsPerDraw,
  groups: draw.groups.map((group) => ({
    groupId: group.id,
    name: group.name,
    ticketFields: group.ticketFields,
    maxWins: group.maxWins,
    awarded: group.awarded,
    // The stored prize list is already group-major, so filtering keeps each group's own order.
    prizes: draw.prizes
      .filter((prize) => prize.groupId === group.id)
      .map((prize) => ({
        prizeId: prize.id,
        name: prize.name,
        quantity: prize.quantity,
        winnersPerDraw: prize.winnersPerDraw,
        awarded: prize.awarded,
      })),
  })),
})

const toPrizePayload = ({ prizeId, name, quantity, winnersPerDraw }) => ({
  // An existing prize keeps its id so already-recorded winners stay attached to it.
  ...(prizeId ? { id: prizeId } : {}),
  name,
  quantity,
  winnersPerDraw,
})

const toGroupPayload = ({ groupId, name, ticketFields, maxWins, prizes }) => ({
  ...(groupId ? { id: groupId } : {}),
  name,
  ticketFields,
  maxWins,
  prizes: prizes.map(toPrizePayload),
})

/**
 * The draw panel: the fields, their rules, and the payload shape. Saving is the page's job — this
 * hands a validated payload to `onSubmit` and stops there.
 */
const Form = ({
  defaultValues,
  onSubmit,
  isSaving,
  submitLabel = 'SUBMIT',
  hasWinners = false,
}) => {
  const { control, handleSubmit, watch, setValue, getValues } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  })

  const { data: sources = [] } = useGetLuckyDrawSources()

  const source = watch('source')
  const ticketFieldOptions = getTicketFieldOptions(sources, source)

  // To autofill names
  const handleSourceChange = (nextSource) => {
    const typedName = getValues('name').trim()
    const nextOptions = getTicketFieldOptions(sources, nextSource)

    setValue('source', nextSource)

    // Name the draw after the source, but only while nothing of the user's own is in there —
    // blank, or still the label we filled in last time.
    if (!typedName || typedName === getSourceLabel(sources, source)) {
      setValue('name', getSourceLabel(sources, nextSource))
    }

    // Ticket types belong to the source, so the previous picks can't survive the switch. A source
    // offering exactly one type offers no choice, so it is filled in rather than asked for.
    getValues('groups').forEach((_, index) =>
      setValue(`groups.${index}.ticketFields`, impliedTicketFields(nextOptions)),
    )
  }

  // Allowlisted rather than spread: `awarded` and the field-array keys are read-only trimmings the
  // server has no business being handed back.
  const submit = (values) =>
    onSubmit({
      name: values.name,
      source: values.source,
      maxWinsPerDraw: values.maxWinsPerDraw,
      groups: values.groups.map(toGroupPayload),
    })

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ my: 3 }}>
              <Typography variant='h2' sx={{ fontSize: '1.5rem' }}>
                Draw Settings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Field
                control={control}
                name='source'
                label='Source'
                select
                disabled={hasWinners}
                helper={hasWinners ? 'Locked — a prize has already been drawn.' : ''}
                onChange={(e) => handleSourceChange(e.target.value)}
              >
                {sources.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Field
                control={control}
                name='maxWinsPerDraw'
                label='Max Prizes Per Dealer'
                type='number'
                helper='Across the whole draw.'
              />
            </Grid>
            <Grid item xs={12}>
              <Field control={control} name='name' label='Name' />
            </Grid>

            {/* A group draws on the source's ticket types, so there is nothing to set up until one
                is picked. */}
            {source ? (
              <GroupList
                control={control}
                getValues={getValues}
                ticketFieldOptions={ticketFieldOptions}
              />
            ) : (
              <Grid item xs={12}>
                <Typography variant='body2' color='text.secondary'>
                  Pick a source to set up its prize groups.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} display='flex' justifyContent='flex-end' gap={2}>
              <Button variant='outlined' component={Link} to='/lucky-draw'>
                CANCEL
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={isSaving}
                endIcon={isSaving && <CircularProgress size={16} />}
              >
                {submitLabel}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  )
}

export default Form
